import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

/* ============================================================
   TYPES
============================================================ */

export type Producto = {
  id_producto: string;
  id_empresa: string;
  nombre: string;
  descripcion: string | null;
  precio: number | null;
  imagen_principal: string | null;
  orden: number;
  activo: boolean;
  fecha_creacion: string;
  fecha_edicion: string | null;
};

export type ProductoInput = {
  nombre: string;
  descripcion?: string | null;
  precio?: number | null;
  orden?: number;
  activo?: boolean;
};

/* ============================================================
   HOOK
============================================================ */

export function useProductos(idEmpresa: string) {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ======================================================
     FETCH
  ====================================================== */

  const fetchProductos = useCallback(async () => {
    if (!idEmpresa) {
      setProductos([]);
      return;
    }

    setLoading(true);
    setError(null);

    const { data, error } = await supabase
      .from('vista_productos_detalle')
      .select('*')
      .eq('id_empresa', idEmpresa)
      .order('orden', { ascending: true });

    if (error) {
      Sentry.captureException(error, { extra: { idEmpresa } });
      setError('Error al cargar productos');
      toast.error('Error al cargar productos');
      setProductos([]);
    } else {
      setProductos((data as Producto[]) || []);
    }

    setLoading(false);
  }, [idEmpresa]);

  useEffect(() => {
    fetchProductos();
  }, [fetchProductos]);

  /* ======================================================
     CREATE
  ====================================================== */

  const createProducto = async (input: ProductoInput) => {
    const idUsuario = localStorage.getItem('id_usuario');

    const { data, error } = await supabase
      .from('productos')
      .insert({
        id_empresa: idEmpresa,
        nombre: input.nombre,
        descripcion: input.descripcion ?? null,
        precio: input.precio ?? null,
        orden: input.orden ?? 0,
        activo: input.activo ?? true,
        usuario_creacion: idUsuario,
      })
      .select('*')
      .single();

    if (error) {
      Sentry.captureException(error, {
        extra: { idEmpresa, nombre: input.nombre },
      });
      toast.error('Error al crear producto');
      return { ok: false, id_producto: null };
    }

    await fetchProductos();
    return { ok: true, producto: data, id_producto: data.id_producto };
  };

  /* ======================================================
     UPDATE
  ====================================================== */

  const updateProducto = async (
    idProducto: string,
    patch: Partial<ProductoInput>
  ) => {
    const idUsuario = localStorage.getItem('id_usuario');

    const { error } = await supabase
      .from('productos')
      .update({ ...patch, usuario_edicion: idUsuario })
      .eq('id_producto', idProducto);

    if (error) {
      Sentry.captureException(error, { extra: { idProducto, idEmpresa } });
      toast.error('Error al actualizar producto');
      return { ok: false };
    }

    await fetchProductos();
    return { ok: true };
  };

  /* ======================================================
     DELETE
  ====================================================== */

  const deleteProducto = async (idProducto: string) => {
    const path = `productos/${idEmpresa}/${idProducto}.jpg`;

    const { error: removeError } = await supabase.storage
      .from('bookoll')
      .remove([path]);

    if (removeError) {
      Sentry.captureException(removeError, {
        extra: { idProducto, idEmpresa },
      });
    }

    const { error } = await supabase
      .from('productos')
      .delete()
      .eq('id_producto', idProducto);

    if (error) {
      Sentry.captureException(error, { extra: { idProducto, idEmpresa } });
      toast.error('Error al eliminar producto');
      return { ok: false };
    }

    toast.success('Producto eliminado');
    await fetchProductos();
    return { ok: true };
  };

  /* ======================================================
     UPLOAD IMAGE
  ====================================================== */

  const uploadImagenProducto = async (idProducto: string, file: File) => {
    const idUsuario = localStorage.getItem('id_usuario');
    const path = `productos/${idEmpresa}/${idProducto}.jpg`;

    const { error: uploadError } = await supabase.storage
      .from('bookoll')
      .upload(path, file, {
        upsert: true,
        contentType: 'image/jpeg',
        cacheControl: '0',
      });

    if (uploadError) {
      Sentry.captureException(uploadError, {
        extra: { idProducto, idEmpresa },
      });
      toast.error('Error al subir la imagen');
      return { ok: false };
    }

    // La ruta no cambia al reemplazar la foto, asi que sin sufijo el navegador
    // y el CDN siguen sirviendo la anterior.
    const publicUrl = `${
      supabase.storage.from('bookoll').getPublicUrl(path).data.publicUrl
    }?v=${Date.now()}`;

    const { error: updateError } = await supabase
      .from('productos')
      .update({ imagen_principal: publicUrl, usuario_edicion: idUsuario })
      .eq('id_producto', idProducto);

    if (updateError) {
      Sentry.captureException(updateError, {
        extra: { idProducto, idEmpresa },
      });
      toast.error('Error al guardar la imagen');
      return { ok: false };
    }

    await fetchProductos();
    return { ok: true, publicUrl };
  };

  /* ======================================================
     REMOVE IMAGE
  ====================================================== */

  const removeImagenProducto = async (idProducto: string) => {
    const idUsuario = localStorage.getItem('id_usuario');
    const path = `productos/${idEmpresa}/${idProducto}.jpg`;

    const { error: removeError } = await supabase.storage
      .from('bookoll')
      .remove([path]);

    if (removeError) {
      Sentry.captureException(removeError, {
        extra: { idProducto, idEmpresa },
      });
    }

    const { error: updateError } = await supabase
      .from('productos')
      .update({ imagen_principal: null, usuario_edicion: idUsuario })
      .eq('id_producto', idProducto);

    if (updateError) {
      Sentry.captureException(updateError, {
        extra: { idProducto, idEmpresa },
      });
      toast.error('Error al eliminar la imagen');
      return { ok: false };
    }

    toast.success('Imagen eliminada');
    await fetchProductos();
    return { ok: true };
  };

  return {
    productos,
    loading,
    error,
    refetch: fetchProductos,
    createProducto,
    updateProducto,
    deleteProducto,
    uploadImagenProducto,
    removeImagenProducto,
  };
}